import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ArrowUpRight, Play, Pause } from 'lucide-react';
import './achieve.css';

import globalai from '../../../assets/achieve/airevolution.jpg';
import greenpower from '../../../assets/achieve/sustainable.jpg';
import digitalisation from '../../../assets/achieve/digitalisation.jpg';
import innovation from '../../../assets/achieve/globalinn.jpg';

const ModernCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const slides = [
    {
      id: 1,
      title: "Revolutionary AI Solutions",
      subtitle: "Transform Your Business",
      description: "Harness the power of artificial intelligence to drive innovation and accelerate growth across every aspect of your organization.",
      image: globalai,
      gradient: "from-purple-900/80 via-blue-900/60 to-transparent",
      cta: "Explore AI",
      accent: "bg-purple-500"
    },
    {
      id: 2,
      title: "Sustainable Future",
      subtitle: "Green Technology",
      description: "Leading the charge in sustainable technology solutions that protect our planet while driving unprecedented business value.",
      image: greenpower,
      gradient: "from-green-900/80 via-emerald-900/60 to-transparent",
      cta: "Go Green",
      accent: "bg-green-500"
    },
    {
      id: 3,
      title: "Digital Transformation",
      subtitle: "Next-Gen Solutions",
      description: "Reimagine your digital landscape with cutting-edge solutions that redefine customer experiences and operational excellence.",
      image: digitalisation,
      gradient: "from-blue-900/80 via-cyan-900/60 to-transparent",
      cta: "Transform Now",
      accent: "bg-blue-500"
    },
    {
      id: 4,
      title: "Global Innovation",
      subtitle: "Worldwide Impact",
      description: "Connect with a global network of innovators and pioneers who are shaping the future of technology and business excellence.",
      image: innovation,
      gradient: "from-orange-900/80 via-red-900/60 to-transparent",
      cta: "Join Network",
      accent: "bg-orange-500"
    }
  ];

  const nextSlide = useCallback(() => {
    setIsLoading(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setIsLoading(false);
    }, 300);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setIsLoading(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
      setIsLoading(false);
    }, 300);
  }, [slides.length]);

  const goToSlide = (index) => {
    if (index !== currentSlide) {
      setIsLoading(true);
      setTimeout(() => {
        setCurrentSlide(index);
        setIsLoading(false);
      }, 300);
    }
  };

  const toggleAutoPlay = () => {
    setIsAutoPlay(!isAutoPlay);
  };

  useEffect(() => {
    if (!isAutoPlay) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlay, nextSlide]);

  const currentSlideData = slides[currentSlide];

  return (
    <div className="modern-carousel">
      
      {/* Background Images */}
      <div className="carousel-bg">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`carousel-bg-slide ${index === currentSlide ? 'active' : ''}`}
          >
            <img src={slide.image} alt="" className="bg-image" />
            <div className={`bg-gradient ${slide.gradient}`} />
          </div>
        ))}
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner" />
        </div>
      )}

      {/* Slide Content */}
      <div className="carousel-content">
        <div className="text-content">
          <div className={`badge ${currentSlideData.accent} ${isLoading ? 'hidden' : ''}`}>
            <span>{currentSlideData.subtitle}</span>
          </div>
          <h1 className={`title ${isLoading ? 'hidden' : ''}`}>{currentSlideData.title}</h1>
          <p className={`description ${isLoading ? 'hidden' : ''}`}>{currentSlideData.description}</p>
          <div className={`cta-wrapper ${isLoading ? 'hidden' : ''}`}>
            <button className="cta-butttoon">
              <span>{currentSlideData.cta}</span>
              <ArrowUpRight className="icon" />
            </button>
          </div>
        </div>
      </div>

      {/* Arrows */}
      <button onClick={prevSlide} className="nav-button left">
        <ChevronLeft className="icon" />
      </button>
      <button onClick={nextSlide} className="nav-button right">
        <ChevronRight className="icon" />
      </button>

      {/* Bottom Controls */}
      <div className="bottom-controls">
        <div className="dots">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className="dot"
              style={
                index === currentSlide
                  ? {
                      backgroundColor: currentSlideData.accent,
                      boxShadow: `0 0 10px ${currentSlideData.accent}`
                    }
                  : {}
              }
            />
          ))}
        </div>

        <button onClick={toggleAutoPlay} className="autoplay-toggle">
          {isAutoPlay ? <Pause className="icon" /> : <Play className="icon" />}
        </button>

        <div className="counter">
          {String(currentSlide + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-bar">
        <div
          className="progress"
          style={{
            width: `${((currentSlide + 1) / slides.length) * 100}%`,
            backgroundColor: currentSlideData.accent
          }}
        />
      </div>


      {/* Floating Elements */}
      <div className="floating-bubble bubble1" />
      <div className="floating-bubble bubble2" />
    </div>
  );
};

export default ModernCarousel;
